import React from 'react';
import Icon from '../../../components/AppIcon';

const SystemHealthMonitor = ({ data }) => {
  const getHealthColor = (value) => {
    if (value >= 90) return 'var(--color-success)';
    if (value >= 70) return 'var(--color-warning)';
    return 'var(--color-error)';
  };

  const getHealthStatus = (value) => {
    if (value >= 90) return 'Excellent';
    if (value >= 70) return 'Good';
    return 'Critical';
  };

  return (
    <div className="card">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
          <Icon name="Activity" size={20} color="var(--color-success)" />
        </div>
        <div>
          <h3 className="text-xl font-heading font-semibold text-foreground">System Health Monitor</h3>
          <p className="text-sm text-muted-foreground">Real-time platform performance</p>
        </div>
      </div>

      <div className="space-y-6">
        <div>
          <h4 className="text-sm font-medium text-foreground mb-4">Server Performance</h4>
          <div className="space-y-3">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">CPU Usage</span>
                <span className="text-sm font-medium text-foreground">{data?.serverPerformance?.cpuUsage}%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="rounded-full h-2 transition-all duration-500"
                  style={{
                    width: `${data?.serverPerformance?.cpuUsage}%`,
                    backgroundColor: getHealthColor(100 - data?.serverPerformance?.cpuUsage)
                  }}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Memory Usage</span>
                <span className="text-sm font-medium text-foreground">{data?.serverPerformance?.memoryUsage}%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="rounded-full h-2 transition-all duration-500"
                  style={{
                    width: `${data?.serverPerformance?.memoryUsage}%`,
                    backgroundColor: getHealthColor(100 - data?.serverPerformance?.memoryUsage)
                  }}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Disk Usage</span>
                <span className="text-sm font-medium text-foreground">{data?.serverPerformance?.diskUsage}%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="rounded-full h-2 transition-all duration-500"
                  style={{
                    width: `${data?.serverPerformance?.diskUsage}%`,
                    backgroundColor: getHealthColor(100 - data?.serverPerformance?.diskUsage)
                  }}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Network Latency</span>
                <span className="text-sm font-medium text-foreground">{data?.serverPerformance?.networkLatency}ms</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="bg-success rounded-full h-2 transition-all duration-500"
                  style={{ width: `${Math.min((100 - data?.serverPerformance?.networkLatency), 100)}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-border pt-6">
          <h4 className="text-sm font-medium text-foreground mb-4">Database Metrics</h4>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-muted/50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Zap" size={16} color="var(--color-accent)" />
                <span className="text-xs text-muted-foreground">Query Performance</span>
              </div>
              <p className="text-xl font-semibold text-foreground">{data?.databaseMetrics?.queryPerformance}%</p>
            </div>
            <div className="bg-muted/50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Database" size={16} color="var(--color-primary)" />
                <span className="text-xs text-muted-foreground">Connection Pool</span>
              </div>
              <p className="text-xl font-semibold text-foreground">{data?.databaseMetrics?.connectionPool}%</p>
            </div>
            <div className="bg-muted/50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="HardDrive" size={16} color="var(--color-warning)" />
                <span className="text-xs text-muted-foreground">Storage Used</span>
              </div>
              <p className="text-xl font-semibold text-foreground">{data?.databaseMetrics?.storageUsed}%</p>
            </div>
            <div className="bg-muted/50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Shield" size={16} color="var(--color-success)" />
                <span className="text-xs text-muted-foreground">Backup Status</span>
              </div>
              <p className="text-sm font-semibold text-success capitalize">{data?.databaseMetrics?.backupStatus}</p>
            </div>
          </div>
        </div>

        <div className="border-t border-border pt-6">
          <h4 className="text-sm font-medium text-foreground mb-4">API Usage Statistics</h4>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total Requests (24h)</span>
              <span className="text-sm font-medium text-foreground">{data?.apiUsage?.totalRequests?.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Avg Response Time</span>
              <span className="text-sm font-medium text-foreground">{data?.apiUsage?.avgResponseTime}ms</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Error Rate</span>
              <span className="text-sm font-medium text-success">{data?.apiUsage?.errorRate}%</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Rate Limit Hits</span>
              <span className="text-sm font-medium text-warning">{data?.apiUsage?.rateLimitHits}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemHealthMonitor;